import * as Sentry from '@sentry/react'
import type { ErrorInfo, ReactNode } from 'react'
import { Component } from 'react'
import { shellCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { CodeBlock } from '@/ui/CodeBlock/CodeBlock'
import { Container } from '@/ui/layout/Container'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import styles from './ErrorBoundary.module.css'

interface Props {
  children: ReactNode
  /** Outside the console frame: there is no `main`, so the fallback brings its own. */
  standalone?: boolean
}

interface State {
  error: Error | null
  componentStack: string | null
}

export function ErrorFallback({
  error,
  componentStack,
  onReset,
  standalone = false,
}: {
  error: unknown
  componentStack?: string | null
  onReset: () => void
  standalone?: boolean
}) {
  const err = error instanceof Error ? error : new Error(String(error))
  const body = (
    <Container size="container-reading" inset>
      <Stack gap="space-16" align="start">
        <Text as="h1" variant="title">
          {shellCopy.error.title}
        </Text>
        <Alert tone="danger">{err.message}</Alert>
        {componentStack && (
          <CodeBlock
            title={err.name}
            caption={shellCopy.error.stackCaption}
            wrap
            className={styles.stack}
          >
            {componentStack}
          </CodeBlock>
        )}
        <Button onClick={onReset}>{shellCopy.error.retry}</Button>
      </Stack>
    </Container>
  )
  return standalone ? (
    <main data-surface="page" className={styles.standalone}>
      {body}
    </main>
  ) : (
    body
  )
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
    // These boundaries catch before the root Sentry.ErrorBoundary, so report
    // here or route crashes never reach Sentry.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    })
    this.setState({ componentStack: info.componentStack ?? null })
  }

  reset = () => this.setState({ error: null, componentStack: null })

  render() {
    const { error, componentStack } = this.state
    if (error) {
      return (
        <ErrorFallback
          error={error}
          componentStack={componentStack}
          onReset={this.reset}
          standalone={this.props.standalone}
        />
      )
    }
    return this.props.children
  }
}
